import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Image from 'gatsby-plugin-sanity-image';
import breakpoints from '../../breakpoints';
import styles from '../../styles';
import Button from '../../button';

const HeroInner = styled.div`
  position: relative;
  min-height: 90px;
  margin-bottom: ${styles.spacing['spacing-lg']};

  @media (min-width: ${breakpoints.md}) {
    height: auto;
    min-height: 150px;
    margin-bottom: ${styles.spacing['spacing-lg']};
  }
`;

export const HeroText = styled.div`
  @media (min-width: ${breakpoints.md}) {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    text-align: left;
    padding: ${styles.spacing['spacing-lg']};
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(3, 1fr);
    height: 100%;
    width: 100%;
  }

  @media (min-width: ${breakpoints.xl}) {
    max-width: ${breakpoints.xl};
    margin-left: calc((100% - ${breakpoints.xl}) / 2);
  }
`;

const HeroImage = styled(Image)`
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;

  @media (max-width: ${breakpoints.md}) {
    min-height: 90px;
  }
`;

const Box = styled.div`
  display: flex;
  flex-direction: column-reverse;
  text-transform: uppercase;
`;

const GridItem = styled.div`
  grid-area: revert;
  text-align: revert;

  h1 {
    @media (max-width: ${breakpoints.md}) {
      font-size: clamp(2.25rem, 3vw + 1rem, 4rem);
      text-shadow: 1px 1px 43px #000;
      margin-bottom: 0;
    }
  }

  p {
    text-shadow: 1px 1px 11px #000;
  }

  hr {
    color: transparent;
    outline: 0;
    border: 0;
    border-bottom: 5px solid ${({ theme }) => theme.colors.brandPrimary};
  }

  ${({ textAlignment }) => {
    switch (textAlignment) {
      case 'topCenter':
        return `
        grid-row: 1;
        grid-column: 2;
      `;
      case 'topRight':
        return `
        grid-row: 1;
        grid-column: 3;
      `;
      case 'middleLeft':
        return `
        grid-row: 2;
        grid-column: 1;
      `;
      case 'middleCenter':
        return `
        grid-row: 2;
        grid-column: 2;
      `;
      case 'middleRight':
        return `
        grid-row: 2;
        grid-column: 3;
      `;
      case 'bottomLeft':
        return `
        grid-row: 3;
        grid-column: 1;
      `;
      case 'bottomCenter':
        return `
        grid-row: 3;
        grid-column: 2;
      `;
      case 'bottomRight':
        return `
        grid-row: 3;
        grid-column: 3;
      `;
      default:
        return `
          grid-row: 1;
          grid-column: 1;
        `;
    }
  }};
`;

function BlockContentHero({ value, getInternalLink }) {
  const { textAlignment, headline1, headline2, mediaItem, enableCta, cta } =
    value;
  return mediaItem?.asset ? (
    <HeroInner textAlignment={textAlignment || ''}>
      <Box>
        <HeroText textAlignment={textAlignment || ''}>
          <GridItem textAlignment={textAlignment || ''}>
            {headline1 && <h1>{headline1}</h1>}

            {headline2 && (
              <>
                <hr aria-hidden="true" />
                <p>{headline2}</p>
                <br />
              </>
            )}

            {enableCta && cta?.caption && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {cta?.linkType === 'internal' ? (
                  <Button to={getInternalLink(cta?.internalLink)}>
                    {cta?.caption}
                  </Button>
                ) : (
                  <Button href={cta?.href}>{cta?.caption}</Button>
                )}
              </>
            )}
          </GridItem>
        </HeroText>
        {mediaItem?.asset && (
          <HeroImage
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...mediaItem}
            options={{
              altFieldName: 'alt',
            }}
          />
        )}
      </Box>
    </HeroInner>
  ) : (
    ''
  );
}

BlockContentHero.propTypes = {
  value: PropTypes.shape({
    textAlignment: PropTypes.string.isRequired,
    headline1: PropTypes.string,
    headline2: PropTypes.string,
    mediaItem: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node,
      PropTypes.shape({}),
    ]).isRequired,
    enableCta: PropTypes.bool,
    cta: PropTypes.shape({
      linkType: PropTypes.string,
      internalLink: PropTypes.shape({}),
      caption: PropTypes.string,
      href: PropTypes.string,
    }),
    presentation: PropTypes.string,
  }),
  getInternalLink: PropTypes.func,
};

BlockContentHero.defaultProps = {
  value: {
    headline1: '',
    headline2: '',
    alt: 'Hero',
    enableCta: false,
    cta: {},
    presentation: '',
  },
  getInternalLink: () => {},
};

export default BlockContentHero;
